import { useQuery } from "@tanstack/react-query";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import GatewayService from "../services/GatewayService";
import UsersService from "@velocity-ehs/user-utils-module";
import {
  getPagination,
  PaginationQueryObject,
  QuerySort,
} from "../components/supportFunctions/graphql/Queries";

export type ApplicationRelationship =
  | "MIXED"
  | "ACCELERATE"
  | "EHS"
  | "OPRISK"
  | "CHEM";

export enum AssignedStatus {
  Active = "Assigned",
  Inactive = "Unassigned",
}

interface Permission {
  id: string;
  name: string;
}

interface PoliciesQuery {
  items: Array<{
    description: string;
    externalAccessAllowed: boolean;
    id: string;
    isVisible: boolean;
    name: string;
    applicationRelationship: String;
    permissions: Array<Permission>;
    isAssignedToRoleId?: Boolean;
  }>;
  pageInfo: {
    totalElements: number;
  };
}

interface PolicyFilter {
  applicationRelationship: ApplicationRelationship;
  customerId: string;
  description?: string;
  isVisible?: boolean;
  name?: string;
  isAssignedToRoleId?: Boolean;
  applicationBundleId?: string | null;
}

function useGetPolicies(params: any, roleId?: string) {
  const { showSnackbar } = useSnackbarActionContext();
  const { filter, pagination } = formatParams(params, roleId);

  const result = useQuery<PoliciesQuery>({
    queryKey: ["policies", params],
    queryFn: () => GatewayService.getPolicies(filter, pagination),
    retry: false,
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
  });

  return {
    ...result,
    policies: result.data?.items || [],
    pagination: (result.data?.pageInfo || {}) as PoliciesQuery["pageInfo"],
  };
}

function formatParams(params: any, roleId?: string) {
  let querySort: QuerySort = null;

  if (params.sortField && params.sortOrder) {
    querySort = {
      sortField: params.sortField,
      sortDirection: params.sortOrder,
    } as QuerySort;
  }

  let pagination: PaginationQueryObject = getPagination(
    params.page + 1,
    params.rows,
    querySort
  );

  const filterValue = params.filters?.isAssignedToRoleId?.value;

  let assignedValue = null;
  if (Array.isArray(filterValue) && filterValue.length === 1) {
    filterValue[0] === AssignedStatus.Active
      ? (assignedValue = true)
      : (assignedValue = false);
  }

  const filter = {
    customerId: UsersService.customer.id,
    name: params.filters?.name.value || undefined,
    isVisible: true,
    roleId: roleId,
    isAssignedToRoleId: assignedValue,
    applicationRelationship: params.filters?.applicationBundle.value || null,
    applicationBundleId: params.filters?.applicationBundleId.value || null,
  } as PolicyFilter;

  return { filter, pagination };
}

export type { PolicyFilter };
export { useGetPolicies };
